import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Bar, Line, Pie } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { rootUrl } from '../../helpers';
import { FaChartBar } from 'react-icons/fa';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, Title, Tooltip, Legend, ArcElement);

const ReportPage = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    try {
      const response = await axios.get(rootUrl(`/get-gold-report`));
      const responseData = response.data.data;
      setData(processData(responseData));
      setError(null);
    } catch (err) {
      setError('Failed to fetch data. Please check the endpoint and try again.');
      setData(null);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const processData = (data) => {
    const groupedData = data.reduce((acc, item) => {
      if (!acc[item.status]) {
        acc[item.status] = { totalValue: 0, totalWeight: 0 };
      }
      acc[item.status].totalValue += (84 * item.weight); // Adjust the calculation if necessary
      acc[item.status].totalWeight += item.weight;
      return acc;
    }, {});

    return groupedData;
  };

  const chartData = data ? {
    labels: Object.keys(data),
    datasets: [
      {
        label: 'Total Value ($)',
        data: Object.values(data).map((item) => item.totalValue),
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1,
      },
    ],
  } : {};

  const chartData2 = data ? {
    labels: Object.keys(data),
    datasets: [
      {
        label: 'Total Weight (grams)',
        data: Object.values(data).map((item) => item.totalWeight),
        backgroundColor: 'rgba(255, 99, 132, 0.6)',
        borderColor: 'rgba(255, 99, 132, 1)',
        borderWidth: 1,
      },
    ],
  } : {};

  // Comparison of Total Value vs Total Weight chart
  const chartDataComparison = data ? {
    labels: Object.keys(data),
    datasets: [
      {
        label: 'Total Value ($)',
        data: Object.values(data).map((item) => item.totalValue),
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1,
        type: 'bar',
      },
      {
        label: 'Total Weight (grams)',
        data: Object.values(data).map((item) => item.totalWeight),
        backgroundColor: 'rgba(255, 99, 132, 0.6)',
        borderColor: 'rgba(255, 99, 132, 1)',
        borderWidth: 1,
        type: 'line',
        fill: false,
      },
    ],
  } : {};

  // Line chart for Total Value over Statuses
  const lineChartData = data ? {
    labels: Object.keys(data),
    datasets: [
      {
        label: 'Total Value ($)',
        data: Object.values(data).map((item) => item.totalValue),
        borderColor: 'rgba(54, 162, 235, 1)',
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        borderWidth: 2,
        fill: true,
      },
    ],
  } : {};

  // Pie chart for the proportion of Total Weight by Status
  const pieChartData = data ? {
    labels: Object.keys(data),
    datasets: [
      {
        data: Object.values(data).map((item) => item.totalWeight),
        backgroundColor: ['rgba(54, 162, 235, 0.6)', 'rgba(255, 99, 132, 0.6)', 'rgba(75, 192, 192, 0.6)', 'rgba(153, 102, 255, 0.6)'],
        hoverBackgroundColor: ['rgba(54, 162, 235, 1)', 'rgba(255, 99, 132, 1)', 'rgba(75, 192, 192, 1)', 'rgba(153, 102, 255, 1)'],
      },
    ],
  } : {};

  return (
    <div className="container mx-auto p-4">
      <div>
        <h2 className="text-xl font-bold flex items-center"><FaChartBar /> Stock Logistics</h2>
      </div>

      <hr className="my-3" />

      {data && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Total Value by Status */}
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h2 className="text-xl font-semibold mb-4">Total Value by Status</h2>
            <Bar data={chartData} options={{ responsive: true }} />
          </div>

          {/* Total Weight by Status */}
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h2 className="text-xl font-semibold mb-4">Total Weight by Status</h2>
            <Bar data={chartData2} options={{ responsive: true }} />
          </div>

        

          {/* Total Value (Line Chart) */}
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h2 className="text-xl font-semibold mb-4">Total Value Over Statuses (Line Chart)</h2>
            <Line data={lineChartData} options={{ responsive: true }} />
          </div>

          {/* Total Weight (Pie Chart) */}
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h2 className="text-xl font-semibold mb-4">Total Weight Proportions (Pie Chart)</h2>
            <Pie data={pieChartData} options={{ responsive: true }} />
          </div>
        </div>
      )}

      {error && (
        <div className="text-red-500 text-center mt-4">
          {error}
        </div>
      )}
    </div>
  );
};

export default ReportPage;
